// Placement des chevrons qui défilent sur le tracé d'itinéraire — en pixels écran, pour garder le
// même espacement quels que soient le zoom et l'inclinaison. Pur, testé dans `routeArrows.test.ts`.

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface PlacedArrow extends ScreenPoint {
  /** Angle du segment, en degrés, sens horaire depuis l'axe des x écran (convention `icon-rotate`). */
  angle: number;
}

/**
 * Un chevron tous les `spacing` pixels le long de la polyligne, le premier à `offset` (pris modulo
 * `spacing`) du départ. Aucun à moins de `margin` pixels des extrémités : ils s'y chevaucheraient
 * avec les repères de départ et d'arrivée.
 */
export function placeArrowsAlongPolyline(points: ScreenPoint[], spacing: number, offset: number, margin = 0): PlacedArrow[] {
  if (points.length < 2 || spacing <= 0) return [];

  let total = 0;
  const lengths: number[] = [];
  for (let i = 1; i < points.length; i++) {
    const length = Math.hypot(points[i]!.x - points[i - 1]!.x, points[i]!.y - points[i - 1]!.y);
    lengths.push(length);
    total += length;
  }

  const arrows: PlacedArrow[] = [];
  let segment = 0;
  let segmentStart = 0;

  for (let at = ((offset % spacing) + spacing) % spacing; at <= total; at += spacing) {
    if (at < margin || at > total - margin) continue;

    while (segment < lengths.length - 1 && segmentStart + lengths[segment]! < at) {
      segmentStart += lengths[segment]!;
      segment++;
    }

    const length = lengths[segment]!;
    if (length === 0) continue;

    const a = points[segment]!;
    const b = points[segment + 1]!;
    const t = (at - segmentStart) / length;

    arrows.push({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI,
    });
  }

  return arrows;
}
